import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Gift, Package, Search, ShoppingCart, Users, Wine, X } from 'lucide-react';
import { Badge } from '../SubscriptionAppBadge';
import { CallFlagBadge } from '../contact-detail/CallFlagBadge';
import { HighlightMatch } from './HighlightMatch';
import { SearchByFilter } from './SearchByFilter';
import { globalSearchIndex, searchAreas, searchByOptions } from '../../data/mockGlobalSearch';
import { matchesSearch } from '../../utils/globalSearch';
import type { GlobalSearchResult, SearchArea, SearchByKey } from '../../types/globalSearch';
const MAX_PER_AREA = 3;
const areaIcons: Record<SearchArea, typeof Users> = {
  contacts: Users,
  subscriptions: Package,
  orders: ShoppingCart,
  loyalty: Gift,
  allocations: Wine
};
type NavItem = {
  key: string;
  to: string;
};

// Dashboard-wide search across contacts, subscriptions, orders, loyalty and allocations
export function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searchBy, setSearchBy] = useState<SearchByKey>('all');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const trimmed = query.trim();
  const groups = useMemo(() => {
    if (!trimmed) return [];
    return searchAreas.map(area => {
      const matches = globalSearchIndex.filter(result => result.area === area.area && matchesSearch(result, trimmed, searchBy));
      return {
        ...area,
        total: matches.length,
        results: matches.slice(0, MAX_PER_AREA)
      };
    }).filter(group => group.total > 0);
  }, [trimmed, searchBy]);

  // Flat list for keyboard navigation: each result, then its "View all" link
  const navItems: NavItem[] = useMemo(() => groups.flatMap(group => [...group.results.map(result => ({
    key: result.id,
    to: result.to
  })), {
    key: `view-all-${group.area}`,
    to: group.viewAllTo
  }]), [groups]);
  useEffect(() => setActiveIndex(0), [trimmed, searchBy]);

  // ⌘K / Ctrl+K focuses the search
  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };
    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const open = (to: string) => {
    setIsOpen(false);
    navigate(to);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (!navItems.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex(i => (i + 1) % navItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(i => (i - 1 + navItems.length) % navItems.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = navItems[activeIndex];
      if (item) open(item.to);
    }
  };
  const activeKey = navItems[activeIndex]?.key;
  const showDropdown = isOpen && trimmed.length > 0;
  const searchByLabel = searchByOptions.find(option => option.value === searchBy)?.label;
  const placeholder = searchBy === 'all' || !searchByLabel ? 'Search customers, subscriptions, orders…' : `Search by ${searchByLabel.toLowerCase()}…`;
  const renderResult = (result: GlobalSearchResult) => {
    const isActive = activeKey === result.id;
    return <li key={result.id} role="option" aria-selected={isActive} id={`gs-${result.id}`} data-id="element-3325" data-mp="yz7v22vf9pkg">
        <button type="button" onMouseEnter={() => setActiveIndex(navItems.findIndex(item => item.key === result.id))} onClick={() => open(result.to)} className={`w-full flex items-center justify-between gap-4 px-4 py-2.5 text-left transition-colors ${isActive ? 'bg-[#F1F5F9]' : 'hover:bg-[#F8FAFC]'}`} data-id="element-3326" data-mp="kalho6pcv7vv">
          <div className="min-w-0" data-id="element-3327" data-mp="gb2u1365gipc">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-3328" data-mp="g7ylxupkd2te">
              <span className="text-sm font-medium text-[#0F172A]" data-id="element-3329" data-mp="1e2f60924i8a">
                <HighlightMatch text={result.title} query={trimmed} data-id="element-3330" data-mp="6bc0p4tbs9qu" />
              </span>
              {result.callFlag && <CallFlagBadge flag={result.callFlag} data-id="element-3331" data-mp="1vqjvenvhai3f" />}
              {result.badge && <Badge variant={result.badge.variant} data-id="element-3332" data-mp="20pwfx1yvnd2u">{result.badge.label}</Badge>}
            </div>
            {result.subtitle && <div className="text-[13px] text-[#64748B] truncate mt-0.5" data-id="element-3333" data-mp="3l7e3mpqv601p">
                <HighlightMatch text={result.subtitle} query={trimmed} data-id="element-3334" data-mp="3g8ueyhkvya5n" />
              </div>}
          </div>
          {result.meta && <span className="text-[13px] text-[#475569] whitespace-nowrap flex-shrink-0" data-id="element-3335" data-mp="3uy8qnwbxp3o7">
              <HighlightMatch text={result.meta} query={trimmed} data-id="element-3336" data-mp="3q7a62kjwxjf5" />
            </span>}
        </button>
      </li>;
  };
  return <div ref={containerRef} className="relative mb-6" data-id="element-3337" data-mp="7s9czkqo8hc1">
      <div className="relative" data-id="element-3338" data-mp="7uvlpnpat2vu">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8] pointer-events-none" data-id="element-3339" data-mp="3s79d61dldu4" />
          <input ref={inputRef} type="text" role="combobox" aria-expanded={showDropdown} aria-controls="global-search-results" aria-activedescendant={showDropdown && activeKey ? `gs-${activeKey}` : undefined} aria-label="Search customers, subscriptions and orders" value={query} onChange={e => {
        setQuery(e.target.value);
        setIsOpen(true);
      }} onFocus={() => setIsOpen(true)} onKeyDown={handleKeyDown} placeholder={placeholder} className="w-full h-12 pl-12 pr-36 bg-white border border-[#CBD5E1] rounded-lg text-[15px] text-[#0F172A] placeholder:text-[#94A3B8] shadow-sm focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[rgba(14,165,233,0.2)] transition-all duration-200" data-id="element-3340" data-mp="hq5jfz8nyo3t" />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2" data-id="element-3341" data-mp="whokg8jd95nl">
            {query && <button type="button" onClick={() => {
          setQuery('');
          inputRef.current?.focus();
        }} className="p-1 rounded text-[#94A3B8] hover:text-[#475569] hover:bg-[#F1F5F9]" aria-label="Clear search" data-id="element-3342" data-mp="11ey7jhja9589">
                <X className="w-4 h-4" data-id="element-3343" data-mp="1f6d30nudmyw7" />
              </button>}
            <kbd className="hidden sm:inline-flex items-center h-6 px-2 rounded border border-[#E2E8F0] bg-[#F8FAFC] text-[12px] font-medium text-[#64748B]" data-id="element-3344" data-mp="1f5thyy9fjudy">
              ⌘K
            </kbd>
            <SearchByFilter value={searchBy} onChange={value => {
          setSearchBy(value);
          inputRef.current?.focus();
        }} data-id="element-3345" data-mp="1p5aqrojzaoe5" />
          </div>
      </div>

      {showDropdown && <div id="global-search-results" className="absolute left-0 right-0 top-full mt-2 bg-white border border-[#E2E8F0] rounded-lg shadow-xl z-[900] max-h-[70vh] overflow-y-auto" data-id="element-3346" data-mp="1u81q4mrguzmh">
          {groups.length === 0 ? <div className="px-6 py-10 text-center" data-id="element-3347" data-mp="1lardoy2wx4fz">
              <p className="text-sm font-medium text-[#0F172A]" data-id="element-3348" data-mp="2hzql6avoa274">No results for '{trimmed}'</p>
              <p className="text-[13px] text-[#64748B] mt-1" data-id="element-3349" data-mp="2czm4okurl7gw">
                {searchBy === 'all' ? 'Check the spelling, or try a name, email, phone, subscription # or order #.' : `You're searching by ${searchByLabel}. Try changing the Search by filter to All.`}
              </p>
            </div> : <div className="py-2 divide-y divide-[#F1F5F9]" data-id="element-3350" data-mp="30powr0yoidfi">
              {groups.map(group => {
          const Icon = areaIcons[group.area];
          const viewAllKey = `view-all-${group.area}`;
          const viewAllActive = activeKey === viewAllKey;
          return <section key={group.area} className="py-1" aria-label={group.label} data-id="element-3351" data-mp="3flpkgf02kpr2">
                    <div className="flex items-center gap-2 px-4 pt-2 pb-1" data-id="element-3352" data-mp="3kfa9vinlkben">
                      <Icon className="w-4 h-4 text-[#0EA5E9]" aria-hidden="true" data-id="element-3353" data-mp="35pmaimyz7mtk" />
                      <h3 className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-3354" data-mp="3aneck9tc4pbh">
                        {group.label}
                      </h3>
                      <span className="text-xs text-[#94A3B8]" data-id="element-3355" data-mp="269qbplzzw0o">{group.total}</span>
                    </div>
                    <ul role="listbox" aria-label={group.label} data-id="element-3356" data-mp="3ofcdhxcw7ewk">
                      {group.results.map(renderResult)}
                      <li role="option" aria-selected={viewAllActive} id={`gs-${viewAllKey}`} data-id="element-3357" data-mp="3tdvztyveespg">
                        <button type="button" onMouseEnter={() => setActiveIndex(navItems.findIndex(item => item.key === viewAllKey))} onClick={() => open(group.viewAllTo)} className={`w-full flex items-center gap-1 px-4 py-2 text-[13px] font-medium text-[#0EA5E9] transition-colors ${viewAllActive ? 'bg-[#F1F5F9]' : 'hover:bg-[#F8FAFC]'}`} data-id="element-3358" data-mp="2cznq54vq8dv8">
                          View all in {group.label}
                          <ArrowRight className="w-3.5 h-3.5" data-id="element-3359" data-mp="1921to3efqb61" />
                        </button>
                      </li>
                    </ul>
                  </section>;
        })}
            </div>}
        </div>}
    </div>;
}